import { fetcher } from '@chipinside/fetcher'
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { shallowEqual, useSelector } from 'react-redux'

import { Permissions } from '#/@types/backend'
import { endpoint } from '#/store/services/endpoints'
import { isLocal } from '#/utils/processEnvironment'
import { sanitizeError } from '#/utils/SanitizeError'

export interface PermissionsState {
  loading: boolean
  permissions: {
    farm: Record<Permissions, boolean>
    company: Record<Permissions, boolean>
    system: Record<Permissions, boolean>
  }
}

const initialState: PermissionsState = {
  loading: false,
  permissions: {
    company: {} as Record<Permissions, boolean>,
    farm: {} as Record<Permissions, boolean>,
    system: {} as Record<Permissions, boolean>,
  },
}

const setDebugCookie = () => {
  if (isLocal) {
    document.cookie = `XDEBUG_SESSION=PHPSTORM;path=/;domain=.${import.meta.env.VITE_SESSION_DOMAIN};SameSite=None; Secure`
  }
}

const getController = (context: keyof PermissionsState['permissions'], slug?: string) => {
  switch (context) {
    case 'farm':
      return endpoint.user.farm_permissions({ farm: slug })
    case 'company':
      return endpoint.user.company_permissions({ company: slug })
    default:
      return endpoint.user.system_permissions()
  }
}

const parsePermissions = (data: Permissions[]) => {
  const permissions = {} as Record<Permissions, boolean>
  data?.forEach(permission => {
    permissions[permission] = true
  })
  return permissions
}

export const loadPermissions = createAsyncThunk(
  'permissions/load',
  async (
    { context = 'system', slug }: { slug?: string; context: keyof PermissionsState['permissions'] },
    thunkAPI,
  ) => {
    try {
      setDebugCookie()
      const controller = getController(context, slug)
      // @ts-ignore
      const { data } = await fetcher<Permissions[]>({ controller })

      return {
        context,
        permissions: parsePermissions(data),
      }
    } catch (error) {
      return thunkAPI.rejectWithValue({ error: sanitizeError(error) })
    }
  },
)

export const permissionsSlice = createSlice({
  extraReducers: builder => {
    builder
      .addCase(loadPermissions.pending, state => {
        state.loading = true
      })
      .addCase(loadPermissions.rejected, (state, { payload }: any) => {
        state.loading = false
        throw payload.error
      })
      .addCase(loadPermissions.fulfilled, (state, action) => {
        state.permissions[action.payload.context] = action.payload.permissions
        state.loading = false
      })
  },
  initialState,
  name: 'permissions',
  reducers: {
    resetPermissions(state, action) {
      const { context } = action.payload
      if (state.permissions && context in state.permissions) {
        state.permissions[context] = {}
      }
    },
  },
  selectors: {
    selectPermissions: state => state.permissions ?? {},
  },
})

export const {
  actions: { resetPermissions },
  selectors: { selectPermissions },
} = permissionsSlice

/**
 * Seletor que retorna as permissões, utilizando o shallowEqual para evitar re-renderizações desnecessárias
 * shallowEqual é uma função que compara os valores de dois objetos é mais performática pois não faz uma comparação profunda
 */
export const usePermissions = () =>
  useSelector(selectPermissions, shallowEqual) as PermissionsState['permissions']

export const permissionsReducer = permissionsSlice.reducer
